import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Link } from 'react-router-dom';

const TableDataLayer = ({ columns, fetchData, totalRows }) => {
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState();
    const [isLoading, setIsLoading] = useState(false);

    // Fetch data with pagination
    const fetchTableData = useCallback(async (page = 1, pageSize = 10, search = '') => {
        // setIsLoading(true);
        try {
            const result = await fetchData(page, pageSize, search);
            setData(result.data || []);
            setTotalRecords(result.total || 0);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [fetchData]);

    // Handle pagination change
    const handlePageChange = (page) => {
        if (page == 0 || page > totalPages) return;
        setCurrentPage(page);
        fetchTableData(page, pageSize);
    };

    // Handle page size change
    const handlePageSizeChange = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setPageSize(newSize);
        fetchTableData(1, newSize); // Reset to page 1 when page size changes
    };

    // Handle page size change
    const handleSearchData = (event) => {
        setSearch(event.target.value);
        setCurrentPage(1);
        fetchTableData(1, pageSize, search);
    };

    // Generate page numbers (adjust as needed for a large number of pages)
    const generatePageNumbers = () => {
        const range = [];
        const maxPagesToShow = 5; // Total pages to display (excluding ellipsis)

        // If there are too many pages, use ellipsis
        if (totalPages <= maxPagesToShow) {
            // Show all pages if there are fewer pages than the maxPagesToShow
            for (let i = 1; i <= totalPages; i++) {
                range.push(i);
            }
        } else {
            let start = Math.max(currentPage - 2, 1); // Show up to two pages before the current page
            let end = Math.min(currentPage + 2, totalPages); // Show up to two pages after the current page

            // Add the first page
            range.push(1);

            // Add ellipsis if necessary
            if (start > 2) {
                range.push('...');
            }

            // Add the pages between the start and end range
            for (let i = start; i <= end; i++) {
                if (i !== 1 && i !== totalPages) {
                    range.push(i);
                }
            }

            // Add ellipsis if necessary
            if (end < totalPages - 1) {
                range.push('...');
            }

            // Add the last page
            if (end < totalPages) {
                range.push(totalPages);
            }
        }

        return range;
    };


    // Effect to load data initially
    useEffect(() => {
        fetchTableData(currentPage, pageSize);
    }, [totalRows]);

    // Calculate total number of pages
    const totalPages = Math.ceil(totalRecords / pageSize);
    // Calculate start and end index for "Showing X to Y of Z entries"
    const startIndex = (currentPage - 1) * pageSize + 1;
    const endIndex = Math.min(currentPage * pageSize, totalRecords);

    return (
        <div className="card-body">
            <div className="d-flex justify-content-between mb-24">
                {/* Page size selection */}
                <div className='d-flex align-items-center gap-2'>
                    <select onChange={handlePageSizeChange} value={pageSize} className="ml-2 form-select radius-8" style={{ width: '70px' }}>
                        {[10, 20, 50, 100].map(size => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select><span>entries per page</span>
                </div>
                <div>
                    <input type="text" onChange={handleSearchData} value={search} className='form-control radius-8' placeholder='Search' />
                </div>
            </div>
            <div className="table-responsive">
                <table className="table bordered-table mb-0" style={{ fontSize: '13px', width: '100%', overflowX: 'scroll' }}>
                    <thead>
                        <tr>
                            {columns.map((col, index) => (
                                <th key={index} scope="col" className={col.className || ""} style={{ fontSize: '13px' }}>
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={columns.length + 1}></td></tr>
                        ) : (
                            data.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map((col, colIndex) => (
                                        <td key={colIndex} className={col.className || ""}>
                                            {col.render ? col.render(row[col.key], row) : row[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {/* Pagination Controls */}
            <div className="pagination-controls d-flex flex-wrap align-items-center gap-2 justify-content-between mt-24">
                <span className="pagination-meta">
                    Showing {startIndex} to {endIndex} of {totalRecords} entries
                </span>
                <ul className="pagination d-flex flex-wrap align-items-center gap-2 mb-0">
                    <li className="page-item">
                        <button
                            type="button"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || totalPages < 1}
                            className="page-link"
                            aria-label="Previous page"
                        >
                            <Icon icon="ep:d-arrow-left" className="text-xl" />
                        </button>
                    </li>
                    {generatePageNumbers().map((pageNum) => (
                        <li key={pageNum} className="page-item">
                            <button
                                type="button"
                                onClick={() => handlePageChange(pageNum)}
                                className={`page-link ${currentPage === pageNum ? "is-active" : ""}`}
                                aria-current={currentPage === pageNum ? "page" : undefined}
                            >
                                {pageNum}
                            </button>
                        </li>
                    ))}
                    <li className="page-item">
                        <button
                            type="button"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages < 1}
                            className="page-link"
                            aria-label="Next page"
                        >
                            <Icon icon="ep:d-arrow-right" className="text-xl" />
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default TableDataLayer;
